import { Component, OnInit, inject, ViewChild } from '@angular/core';
import { ClientsService } from '../../core/services/clients.service';
import { MealRecordsService } from '../../core/services/meal-records.service';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration, ChartType } from 'chart.js';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, MatCardModule, MatIconModule, TranslateModule, BaseChartDirective],
  styles: [`
    .page-header {
      margin-bottom: 24px;
    }
    .page-title {
      margin: 0;
      font-size: 28px;
      font-weight: 600;
      color: #1F2937;
    }
    .stats-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
      gap: 20px;
      margin-bottom: 24px;
    }
    .stat-card {
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.08);
      transition: all 0.3s ease;
      border: 1px solid #E5E7EB;
    }
    .stat-card:hover {
      box-shadow: 0 4px 16px rgba(0,0,0,0.12);
      transform: translateY(-2px);
    }
    .stat-card-content {
      padding: 24px;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    .stat-info h3 {
      margin: 0 0 8px 0;
      font-size: 13px;
      font-weight: 600;
      color: #6B7280;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
    .stat-info h1 {
      margin: 0;
      font-size: 36px;
      font-weight: 700;
      color: #1F2937;
    }
    .stat-icon {
      width: 64px;
      height: 64px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .stat-icon mat-icon {
      font-size: 32px;
      width: 32px;
      height: 32px;
      color: white;
    }
    .stat-icon.primary {
      background: linear-gradient(135deg, #3B82F6 0%, #2563EB 100%);
    }
    .stat-icon.success {
      background: linear-gradient(135deg, #10B981 0%, #059669 100%);
    }
    .stat-icon.info {
      background: linear-gradient(135deg, #06B6D4 0%, #0891B2 100%);
    }
    .stat-icon.warning {
      background: linear-gradient(135deg, #F59E0B 0%, #D97706 100%);
    }
    .chart-card {
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.08);
      border: 1px solid #E5E7EB;
    }
    .chart-card-header {
      padding: 24px 24px 16px 24px;
      border-bottom: 1px solid #E5E7EB;
    }
    .chart-card-header h2 {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
      color: #1F2937;
    }
    .chart-card-header p {
      margin: 4px 0 0 0;
      font-size: 14px;
      color: #666;
    }
    .chart-card-body {
      padding: 24px;
    }
  `],
  template: `
    <div class="page-header">
      <h1 class="page-title">{{ 'dashboard.title' | translate }}</h1>
    </div>

    <div class="stats-grid">
      <mat-card class="stat-card">
        <div class="stat-card-content">
          <div class="stat-info">
            <h3>{{ 'dashboard.activeClients' | translate }}</h3>
            <h1>{{ activeClients }}</h1>
          </div>
          <div class="stat-icon primary">
            <mat-icon>people</mat-icon>
          </div>
        </div>
      </mat-card>

      <mat-card class="stat-card">
        <div class="stat-card-content">
          <div class="stat-info">
            <h3>{{ 'dashboard.todayRecords' | translate }}</h3>
            <h1>{{ todayCount }}</h1>
          </div>
          <div class="stat-icon success">
            <mat-icon>today</mat-icon>
          </div>
        </div>
      </mat-card>

      <mat-card class="stat-card">
        <div class="stat-card-content">
          <div class="stat-info">
            <h3>{{ 'dashboard.weeklyRecords' | translate }}</h3>
            <h1>{{ weeklyCount }}</h1>
          </div>
          <div class="stat-icon info">
            <mat-icon>date_range</mat-icon>
          </div>
        </div>
      </mat-card>

      <mat-card class="stat-card">
        <div class="stat-card-content">
          <div class="stat-info">
            <h3>{{ 'dashboard.monthlyRecords' | translate }}</h3>
            <h1>{{ monthlyCount }}</h1>
          </div>
          <div class="stat-icon warning">
            <mat-icon>calendar_month</mat-icon>
          </div>
        </div>
      </mat-card>
    </div>

    <mat-card class="chart-card">
      <div class="chart-card-header">
        <h2>{{ 'dashboard.weeklyTrend' | translate }}</h2>
        <p>{{ 'dashboard.weeklyTrendSubtitle' | translate }}</p>
      </div>
      <div class="chart-card-body">
        <canvas baseChart
          [data]="chartData"
          [options]="chartOptions"
          [type]="chartType">
        </canvas>
      </div>
    </mat-card>
  `
})
export class DashboardComponent implements OnInit {
  private clients = inject(ClientsService);
  private meals = inject(MealRecordsService);

  activeClients = 0;
  todayCount = 0;
  weeklyCount = 0;
  monthlyCount = 0;

  public chartType: ChartType = 'line';
  public chartData: ChartConfiguration['data'] = {
    datasets: [],
    labels: []
  };
  public chartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 2.5,
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 10
        }
      }
    }
  };

  ngOnInit(): void {
    this.clients.activeCount().subscribe(v => (this.activeClients = v));
    this.meals.todayCount().subscribe(v => (this.todayCount = v));
    this.loadWeeklyData();
    this.loadWeeklyCount();
    this.loadMonthlyCount();
  }

  private loadWeeklyData(): void {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    monday.setHours(0, 0, 0, 0);

    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);

    const fromISO = new Date(Date.UTC(monday.getFullYear(), monday.getMonth(), monday.getDate(), 12, 0, 0)).toISOString();
    const toISO = new Date(Date.UTC(sunday.getFullYear(), sunday.getMonth(), sunday.getDate(), 12, 0, 0)).toISOString();

    this.meals.getAll({ from: fromISO, to: toISO }).subscribe(records => {
      const weekDays = ['Pon', 'Uto', 'Sre', 'Čet', 'Pet', 'Sub', 'Ned'];
      const dailyData: { [key: string]: { breakfast: number, lunch: number, dinner: number, lunchPackage: number, total: number } } = {};

      // Initialize all days
      for (let i = 0; i < 7; i++) {
        const day = new Date(monday);
        day.setDate(monday.getDate() + i);
        const dateKey = day.toISOString().split('T')[0];
        dailyData[dateKey] = { breakfast: 0, lunch: 0, dinner: 0, lunchPackage: 0, total: 0 };
      }

      // Aggregate data
      records.forEach(record => {
        const dateKey = record.date.split('T')[0];
        if (dailyData[dateKey]) {
          dailyData[dateKey].breakfast += record.breakfastQty || 0;
          dailyData[dateKey].lunch += record.lunchQty || 0;
          dailyData[dateKey].dinner += record.dinnerQty || 0;
          dailyData[dateKey].lunchPackage += record.lunchPackageQty || 0;
          dailyData[dateKey].total += (record.breakfastQty || 0) + (record.lunchQty || 0) + (record.dinnerQty || 0) + (record.lunchPackageQty || 0);
        }
      });

      const sortedDates = Object.keys(dailyData).sort();
      const labels = sortedDates.map((_, index) => weekDays[index]);
      const breakfastData = sortedDates.map(date => dailyData[date].breakfast);
      const lunchData = sortedDates.map(date => dailyData[date].lunch);
      const dinnerData = sortedDates.map(date => dailyData[date].dinner);
      const lunchPackageData = sortedDates.map(date => dailyData[date].lunchPackage);

      this.chartData = {
        labels: labels,
        datasets: [
          {
            label: 'Doručak',
            data: breakfastData,
            borderColor: '#667eea',
            backgroundColor: 'rgba(102, 126, 234, 0.1)',
            tension: 0.4,
            fill: true
          },
          {
            label: 'Ručak',
            data: lunchData,
            borderColor: '#f093fb',
            backgroundColor: 'rgba(240, 147, 251, 0.1)',
            tension: 0.4,
            fill: true
          },
          {
            label: 'Večera',
            data: dinnerData,
            borderColor: '#4facfe',
            backgroundColor: 'rgba(79, 172, 254, 0.1)',
            tension: 0.4,
            fill: true
          },
          {
            label: 'Lunch Paket',
            data: lunchPackageData,
            borderColor: '#f5576c',
            backgroundColor: 'rgba(245, 87, 108, 0.1)',
            tension: 0.4,
            fill: true
          }
        ]
      };
    });
  }

  private loadWeeklyCount(): void {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    monday.setHours(0, 0, 0, 0);

    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);

    const fromISO = new Date(Date.UTC(monday.getFullYear(), monday.getMonth(), monday.getDate(), 12, 0, 0)).toISOString();
    const toISO = new Date(Date.UTC(sunday.getFullYear(), sunday.getMonth(), sunday.getDate(), 12, 0, 0)).toISOString();

    this.meals.getAll({ from: fromISO, to: toISO }).subscribe(records => {
      this.weeklyCount = records.reduce((sum, r) =>
        sum + (r.breakfastQty || 0) + (r.lunchQty || 0) + (r.dinnerQty || 0) + (r.lunchPackageQty || 0), 0
      );
    });
  }

  private loadMonthlyCount(): void {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    firstDay.setHours(0, 0, 0, 0);

    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    lastDay.setHours(23, 59, 59, 999);

    const fromISO = new Date(Date.UTC(firstDay.getFullYear(), firstDay.getMonth(), firstDay.getDate(), 12, 0, 0)).toISOString();
    const toISO = new Date(Date.UTC(lastDay.getFullYear(), lastDay.getMonth(), lastDay.getDate(), 12, 0, 0)).toISOString();

    this.meals.getAll({ from: fromISO, to: toISO }).subscribe(records => {
      this.monthlyCount = records.reduce((sum, r) =>
        sum + (r.breakfastQty || 0) + (r.lunchQty || 0) + (r.dinnerQty || 0) + (r.lunchPackageQty || 0), 0
      );
    });
  }
}
